<script setup lang="ts">
// Renders the toast store as a fixed bottom-right stack (bottom-center on
// phones, above the cookie banner). Mounted once in App.vue. At most
// MAX_TOASTS show (the store drops the oldest); hover or focus pauses a
// toast's auto-dismiss. Tone is a 1px left hairline plus the icon tile.
// While the mini cart is open on lg+, the stack moves left of its 400px panel
// (+24px gutter) so an Undo never covers the panel's footer.
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Check, CircleAlert, Info, X } from 'lucide-vue-next';
import { useToastStore, type Toast } from '../stores/toast';
import { useUiStore } from '../stores/ui';

const toasts = useToastStore();
const ui = useUiStore();
const { t } = useI18n();

const tone = (toast: Toast) => toast.tone ?? 'success';
const HAIRLINE = { success: 'border-l-success/70', error: 'border-l-error/70', default: '' } as const;
const TILE = { success: 'bg-success/15 text-success', error: 'bg-error/15 text-error', default: 'bg-primary/12 text-primary' } as const;
const ICON = { success: Check, error: CircleAlert, default: Info } as const;

function runAction(toast: Toast) {
  toast.action?.onClick?.();
  toasts.dismiss(toast.id);
}

const actionClass = 'shrink-0 rounded-md bg-primary px-3 py-1.5 font-display text-[12px] font-semibold text-primary-ink hover:bg-primary-hover';
</script>

<template>
  <div
    class="pointer-events-none fixed inset-x-4 bottom-24 z-[60] flex flex-col items-center gap-2 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:items-end"
    :class="ui.miniCartOpen && 'lg:right-[424px]'"
    aria-live="polite"
  >
    <!-- Enter uses the `animate-pop` easing (DESIGN.md Motion); exit is a plain fade. -->
    <TransitionGroup
      enter-active-class="transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
      leave-active-class="transition-opacity duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="translate-y-3 scale-95 opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-for="toast in toasts.items"
        :key="toast.id"
        role="status"
        class="pointer-events-auto flex w-full max-w-[380px] items-center gap-3 rounded-xl border border-border bg-surface/95 p-3 shadow-lift backdrop-blur"
        :class="HAIRLINE[tone(toast)]"
        @mouseenter="toasts.pause(toast.id)"
        @mouseleave="toasts.resume(toast.id)"
        @focusin="toasts.pause(toast.id)"
        @focusout="toasts.resume(toast.id)"
      >
        <img v-if="toast.image" :src="toast.image" alt="" class="h-11 w-[72px] shrink-0 rounded-md object-cover" />
        <span v-else class="grid size-9 shrink-0 place-items-center rounded-md" :class="TILE[tone(toast)]">
          <component :is="ICON[tone(toast)]" class="size-4" :stroke-width="2.25" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="truncate text-[14px] font-semibold text-text">{{ toast.title }}</p>
          <p v-if="toast.body" class="truncate text-[12px] text-text-dim">{{ toast.body }}</p>
        </div>
        <button v-if="toast.action?.onClick" type="button" :class="actionClass" @click="runAction(toast)">{{ toast.action.label }}</button>
        <RouterLink v-else-if="toast.action?.to" :to="toast.action.to" :class="actionClass" @click="toasts.dismiss(toast.id)">{{ toast.action.label }}</RouterLink>
        <button type="button" :aria-label="t('common.close')" class="grid size-7 shrink-0 place-items-center rounded-md text-text-mute hover:bg-card hover:text-text" @click="toasts.dismiss(toast.id)">
          <X class="size-4" :stroke-width="2" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>
